import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AbsenceService } from 'src/app/data/absence.service';
import { Filter } from 'src/app/models/common/filter';
import { RomanMonths } from 'src/app/models/common/roman-months';
import { AbsencesPerYearDto } from 'src/app/models/dtos/absences/absences-per-year-dto';
import { formatAbsence } from 'src/app/common/utils/absence-utils';

interface PagingRequest {
  Year: number;
  Page: number;
  PageSize: number;
  SortingDirection: string;
  SortingField: string;
  Filters: Filter[]
}

@Injectable({
  providedIn: 'root',
})
export class AbsencesPerYearListDataSource {
  private absenceService = inject(AbsenceService);

  countSubject = new BehaviorSubject<number>(0);
  count$: Observable<number> = this.countSubject.asObservable();
  isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  absencesSubject = new Subject<PagingRequest>();

  private readonly romanMonths: string[] = RomanMonths.MONTHS;

  absences$: Observable<AbsencesPerYearDto[]> = this.absencesSubject.pipe(
    tap(() => this.isLoadingSubject.next(true)),
    switchMap((request) =>
      this.absenceService.getAbsencesPerYear(request.Year, request.Page, request.PageSize, request.SortingField, request.SortingDirection, request.Filters)
        .pipe(
          tap((res) => this.countSubject.next(res.Count)),
          map((res) => this.addMonthProperties(res.Results)),
          takeUntil(this.cancelationSubject),
          finalize(() => this.isLoadingSubject.next(false))
        )
    )
  );

  private addMonthProperties(data: AbsencesPerYearDto[]): AbsencesPerYearDto[] {
    return data.map(item => {
      this.romanMonths.forEach(month => {
        (item as any)[month] = '-';
      });

      if (item.Summaries && Array.isArray(item.Summaries)) {
        item.Summaries.forEach(summary => {
          if (summary.MonthNumber) {
            (item as any)[RomanMonths.toRoman(summary.MonthNumber)] = formatAbsence(summary.AbsenceFullDays, summary.AbsenceHours);
          }
        });
      }

      return item;
    });
  }

  private cancelationSubject = new Subject();

  constructor() { }
}
